import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { lightColors } from '../theme/theme';
import { httpService } from '../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../services';

export interface ChatBotItem {
  id: string;
  bot_id: string;
  bot_name: string;
  welcome_message: string;
  no_result_message: string;
  handover_message: string;
  status?: string;
  created_at?: string;
  avatarColor: string;
}

const extractBots = (decrypted: any): any[] => {
  if (Array.isArray(decrypted)) return decrypted;
  if (Array.isArray(decrypted?.chat_bots)) return decrypted.chat_bots;
  if (Array.isArray(decrypted?.chatBots)) return decrypted.chatBots;
  if (Array.isArray(decrypted?.bots)) return decrypted.bots;
  if (Array.isArray(decrypted?.data)) return decrypted.data;
  return [];
};

const useApplicationChatBots = () => {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const [bots, setBots] = useState<ChatBotItem[]>([]);
  const [isLoadingBots, setIsLoadingBots] = useState(true);

  const organizationId = userDetails?.organization_id || '';
  const applicationId = appId || '';

  const fetchChatBots = useCallback(async (): Promise<void> => {
    if (!organizationId || !applicationId) {
      setBots([]);
      setIsLoadingBots(false);
      return;
    }

    setIsLoadingBots(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/getApplicattionChatBots`,
        payload
      );

      if (response.data?.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<any>(
          response.data.data,
          getSessionKey() as string
        );

        const transformedBots: ChatBotItem[] = extractBots(decrypted).map((bot: any, index: number) => ({
          id: bot._id || bot.bot_id || bot.id || `bot-${index}`,
          bot_id: bot.bot_id || bot._id || bot.id || `bot-${index}`,
          bot_name: bot.bot_name || bot.name || 'Untitled Bot',
          welcome_message: bot.welcome_message || '',
          no_result_message: bot.no_result_message || '',
          handover_message: bot.handover_message || '',
          status: bot.status || bot.bot_status || 'Active',
          created_at: bot.created_at,
          avatarColor: lightColors[index % lightColors.length],
        }));

        setBots(transformedBots);
      } else {
        setBots([]);
      }
    } catch (error) {
      console.log('Error fetching chat bots:', error);
      toast.error('Failed to load chat bots. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setBots([]);
    } finally {
      setIsLoadingBots(false);
    }
  }, [organizationId, applicationId]);

  return { bots, isLoadingBots, fetchChatBots, appId: applicationId };
};

export default useApplicationChatBots;
